import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Table, Divider } from 'rsuite';
import { AxiosError } from 'axios';
import TablePagination from '@/components/TablePagination';
import { BaseErrorModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import { PointEarnRecord, PointEarnRule } from '@/models/point-account.model';
import { getPagedPointEarnRecords } from '@/services/point-account.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;

interface Props extends DrawerProps {
  pointAccountId: number;
  rule?: PointEarnRule;
}

const PointEarnRecordDrawer = (props: Props) => {
  const { open, onClose, pointAccountId, rule, ...rest } = props;
  const [filterReq, setFilterReq] = useState<PaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<PointEarnRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && rule) fetchData();
  }, [open, rule, filterReq]);

  const fetchData = async () => {
    if (!rule) return;
    setLoading(true);
    try {
      const result = await getPagedPointEarnRecords(pointAccountId, rule.id, filterReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching earn records: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer size="md" placement="right" open={open} onClose={onClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>Earn records: {rule?.name}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <Table height={500} data={items} loading={loading}>
          <Column flexGrow={0.6}>
            <HeaderCell>Id</HeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column flexGrow={0.8}>
            <HeaderCell>User Id</HeaderCell>
            <Cell dataKey="userId" />
          </Column>
          <Column flexGrow={1}>
            <HeaderCell>Participation Id</HeaderCell>
            <Cell dataKey="sourceId" />
          </Column>
          <Column flexGrow={1}>
            <HeaderCell>Point Tx Id</HeaderCell>
            <Cell dataKey="pointTransactionId" />
          </Column>
          <Column flexGrow={1.5}>
            <HeaderCell>Created At</HeaderCell>
            <Cell fullText>{(row: PointEarnRecord) => jsDateToZone(row.createdAt)}</Cell>
          </Column>
        </Table>
        <Divider />
        <TablePagination
          totalRecords={pageRes.totalRecords}
          pageSize={pageRes.pageSize}
          activePage={pageRes.pageNumber}
          onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
          onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
        />
      </Drawer.Body>
    </Drawer>
  );
};

export default PointEarnRecordDrawer;
